import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { InlineView } from '@/components/ui/inline-view';
import { EditLockBanner } from '@/components/ui/edit-lock-banner';
import { useEditLock } from '@/hooks/useEditLock';
import { FloatingPanel } from '@/components/ui/floating-panel';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { toast } from 'sonner';
import { buildOrderPatch, pierdeReparto } from './order-patch';

interface ModelOption { id: string; code: string; name: string }
interface Schedule { quantity: number; due_date: string }
interface LineDraft {
  /** Id de la línea original: la configuración y los hitos se conservan por id, no por posición. */
  id?: string;
  modelId: string;
  quantity: string;
  unitPrice: string;
  configuration?: Record<string, unknown>;
  schedules?: Schedule[];
}

interface OrderDetail {
  id: string; number: string; status: string;
  promised_date: string | null; notes: string | null;
  lines: {
    id: string; model_id: string; quantity: number; unit_price: number;
    configuration?: Record<string, unknown>;
    schedules?: { quantity: number; due_date: string }[];
  }[];
}

/**
 * Retener un pedido: el motivo es obligatorio porque es lo que se pregunta después, cuando
 * alguien mira el pedido tres semanas más tarde y no sabe por qué está parado.
 */
export function HoldDialog({ orderId, number, open, onOpenChange, onDone }: {
  orderId: string | null; number: string; open: boolean;
  onOpenChange: (open: boolean) => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Se limpia al abrir **y al cambiar de pedido**: la ventana no es modal, así que se puede pulsar
  // «Retener» en otra fila sin cerrarla, y el motivo escrito para uno no puede viajar al otro.
  useEffect(() => { if (open) setReason(''); }, [open, orderId]);

  const submit = async () => {
    if (!orderId) return;
    if (reason.trim().length < 3) { toast.error('Escribe por qué se retiene'); return; }
    setSaving(true);
    try {
      await api(`/sales-orders/${orderId}/hold`, { method: 'POST', body: { reason: reason.trim() } });
      toast.success('Pedido retenido');
      onDone();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FloatingPanel
      open={open} onClose={() => onOpenChange(false)}
      title={`Retener el pedido ${number}`}
      description="No pasa a producción ni se expide hasta levantarla."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Volver</Button>
        <Button loading={saving} onClick={() => void submit()}>Retener</Button></>}
    >
      <Field label="Motivo" required hint="Impago, pendiente de firma, dirección sin confirmar…">
        <Textarea aria-label="Motivo de la retención" value={reason} data-autofocus
          onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} />
      </Field>
    </FloatingPanel>
  );
}

/** Cancelar un pedido pide el motivo en la misma ventana flotante, no en el `prompt` del navegador. */
export function CancelOrderDialog({ orderId, number, open, onOpenChange, onDone }: {
  orderId: string | null; number: string; open: boolean;
  onOpenChange: (open: boolean) => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Igual que al retener: si se pulsa «Cancelar» en otra fila sin cerrar la ventana, el motivo
  // escrito para el primero no puede viajar al segundo.
  useEffect(() => { if (open) setReason(''); }, [open, orderId]);

  const submit = async () => {
    if (!orderId) return;
    setSaving(true);
    try {
      await api(`/sales-orders/${orderId}/cancel`, { method: 'POST', body: { reason: reason.trim() || null } });
      toast.success('Pedido cancelado');
      onDone();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FloatingPanel
      open={open} onClose={() => onOpenChange(false)}
      title={`Cancelar el pedido ${number}`}
      description="Deja de contar como trabajo pendiente; se conserva con su historia."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Volver</Button>
        <Button variant="destructive" loading={saving} onClick={() => void submit()}>Cancelar pedido</Button></>}
    >
      <Field label="Motivo" hint="Opcional, pero ayuda a saber por qué se cayó.">
        <Textarea aria-label="Motivo de la cancelación" value={reason} data-autofocus
          onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} />
      </Field>
    </FloatingPanel>
  );
}

/** Editar un pedido en borrador o confirmado mientras no tenga unidades en fábrica. */
export function EditOrderDialog({ orderId, open, onOpenChange, onDone }: {
  orderId: string | null; open: boolean;
  onOpenChange: (open: boolean) => void; onDone: () => void;
}) {
  const [promisedDate, setPromisedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  // Se guarda lo que había para saber si las líneas se han tocado: reenviarlas sin cambios
  // borraría la configuración de cada espejo y reduciría los hitos de entrega a uno.
  const [originales, setOriginales] = useState<LineDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El bloqueo se pide al abrir la edición y se suelta al cerrarla: retener, confirmar o cancelar
  // no pasan por aquí, porque no son editar la ficha.
  const lock = useEditLock('sales_order', open ? orderId : null);

  const detail = useQuery({
    queryKey: ['order-edit', orderId],
    queryFn: () => api<OrderDetail>(`/sales-orders/${orderId}`),
    enabled: open && Boolean(orderId)
  });
  const models = useQuery({
    queryKey: ['models', 'all'],
    // La edición tiene que poder enseñar el modelo que ya lleva la línea aunque esté archivado:
    // el servidor solo rechaza los archivados en líneas nuevas.
    queryFn: () => api<{ data: ModelOption[] }>('/mirror-models?active=all'),
    enabled: open
  });

  useEffect(() => {
    const data = detail.data;
    if (!open || !data) return;
    setPromisedDate(data.promised_date ?? '');
    setNotes(data.notes ?? '');
    const cargadas = data.lines.map((line) => ({
      id: line.id,
      modelId: line.model_id,
      quantity: String(line.quantity),
      unitPrice: String(line.unit_price),
      configuration: line.configuration ?? {},
      schedules: line.schedules?.map((schedule) => ({ quantity: schedule.quantity, due_date: schedule.due_date }))
    }));
    setLines(cargadas);
    setOriginales(cargadas.map((line) => ({ ...line })));
    setError(null);
  }, [open, detail.data]);

  const updateLine = (index: number, key: keyof LineDraft, value: string) => {
    setLines((current) => current.map((line, i) => i === index ? { ...line, [key]: value } : line));
  };

  const submit = async () => {
    if (!orderId) return;
    setSaving(true); setError(null);
    try {
      const valid = lines.filter((line) => line.modelId && Number(line.quantity) > 0);
      if (!valid.length) throw new Error('El pedido necesita al menos una línea');
      if (valid.some((line) => !Number.isFinite(Number(line.unitPrice)))) {
        throw new Error('El precio de una línea no es un número');
      }
      const cuerpo = buildOrderPatch({ promisedDate, notes, validas: valid, originales });
      const avisoReparto = pierdeReparto(valid, originales);
      await api(`/sales-orders/${orderId}`, { method: 'PATCH', body: cuerpo });
      toast.success('Pedido actualizado');
      if (avisoReparto) {
        toast.warning('Al cambiar la cantidad, la entrega partida de esa línea se ha quedado en un solo hito');
      }
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="lg"
      title={`Editar el pedido ${detail.data?.number ?? ''}`}
      description="Se puede editar mientras no haya unidades fabricadas: en cuanto existen, el pedido es historia."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button loading={saving} disabled={detail.isLoading || !lock.canEdit} onClick={() => void submit()}>
          Guardar cambios
        </Button></>}
    >
      <div className="space-y-4">
        {/* Fuera del fieldset: si no, sus botones quedan deshabilitados en solo lectura. */}
        <EditLockBanner
          state={lock.state}
          onTakeOver={() => void lock.takeOver()}
          onRetry={() => void lock.retry()}
        />
        <fieldset disabled={!lock.canEdit} className="m-0 min-w-0 space-y-4 border-0 p-0">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Entrega prometida">
            <Input type="date" value={promisedDate} onChange={(e) => setPromisedDate(e.target.value)} />
          </Field>
          <Field label="Notas">
            <Input value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Líneas</p>
            <Button variant="outline" size="sm"
              onClick={() => setLines((current) => [...current, { modelId: '', quantity: '1', unitPrice: '0' }])}>
              <Plus /> Añadir
            </Button>
          </div>
          {lines.map((line, index) => (
            <div key={index} className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_auto]">
              <Select value={line.modelId} onChange={(e) => updateLine(index, 'modelId', e.target.value)}>
                <option value="">Modelo…</option>
                {(models.data?.data ?? []).map((model) => (
                  <option key={model.id} value={model.id}>{model.code} · {model.name}</option>
                ))}
              </Select>
              <Input type="number" min={1} value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} placeholder="Cantidad" />
              <Input type="number" min={0} step="0.01" value={line.unitPrice} onChange={(e) => updateLine(index, 'unitPrice', e.target.value)} placeholder="Precio" />
              <Button variant="ghost" size="icon-sm" title="Quitar línea"
                onClick={() => setLines((current) => current.length > 1 ? current.filter((_, i) => i !== index) : current)}>
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
        </fieldset>
      </div>
    </InlineView>
  );
}
